import { ImageWithFallback, Typography } from '@/components';
import { MasterTranslation, TextStyles } from '@/constants';
import { useClickOutsideMultiElement, useGlobalState } from '@/hooks';
import {
  type ChangeEventHandler,
  type KeyboardEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dropdown,
  DropdownInput,
  DropdownInputWrapper,
  DropdownItem,
  DropdownList,
  ListWrapper,
  NotFound,
  SearchIcon,
  SearchIconContainer,
  SelectorContainer,
} from './Selector.styles';

export interface IItem {
  name: string;
  id: string;
  logo?: string;
}

interface SelectorProps<T> {
  placeholder?: string;
  dropdownList: T[];
  defaultLogo: string;
  disabled?: boolean;
  disabledCustomSearch?: boolean;
  defaultSelectedItem?: T | null;
  onSelect?: (selectedItem: T | null) => void;
  onCustomSearchClicked?: () => void;
  ariaLabel?: string;
  inputAriaLabel?: string;
}

const Selector = <T extends IItem>({
  placeholder,
  defaultLogo,
  dropdownList,
  disabled,
  onSelect,
  defaultSelectedItem,
  onCustomSearchClicked,
  disabledCustomSearch,
  ariaLabel,
  inputAriaLabel,
}: SelectorProps<T>): ReactElement => {
  const { t } = useGlobalState();

  const [list, setList] = useState<T[]>(dropdownList || []);
  const [isOpen, setIsOpen] = useState<boolean | null>(null);
  const [currentItem, setCurrentItem] = useState<T | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filteredList = list?.filter((item) => item?.id !== currentItem?.id) || [];

  // Generate stable unique IDs
  const listboxId = useMemo(() => `listbox-${Math.random().toString(36).substr(2, 9)}`, []);
  const comboboxId = useMemo(() => `combobox-${Math.random().toString(36).substr(2, 9)}`, []);

  const handleCustomSearchFunction = () => {
    !disabledCustomSearch && onCustomSearchClicked && onCustomSearchClicked();
  };

  const setInputValue = (value: string) => {
    if (inputRef?.current) {
      inputRef.current.value = value;
    }
  };

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const searchValue = e.target.value;
    const searchList = !searchValue
      ? dropdownList
      : dropdownList?.filter((item) => item.name?.toLowerCase()?.includes(searchValue.toLowerCase()));
    setList(searchList);
    setCurrentItem(null);
    setHighlightedIndex(-1);
    onSelect?.(null);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSelect = (selectedItem: T) => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    dropdownListRef.current?.scroll({ top: 0 });

    if (selectedItem.id === currentItem?.id) return;

    setList(dropdownList);
    setCurrentItem(selectedItem);
    setInputValue(selectedItem.name);
    onSelect?.(selectedItem);

    // Return focus to input after selection
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          const nextIndex = highlightedIndex < filteredList.length - 1 ? highlightedIndex + 1 : 0;
          setHighlightedIndex(nextIndex);
          // Scroll to highlighted item
          itemRefs.current[nextIndex]?.scrollIntoView({ block: 'nearest' });
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(filteredList.length - 1);
        } else {
          const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : filteredList.length - 1;
          setHighlightedIndex(prevIndex);
          // Scroll to highlighted item
          itemRefs.current[prevIndex]?.scrollIntoView({ block: 'nearest' });
        }
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && filteredList[highlightedIndex]) {
          handleSelect(filteredList[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      }
      case 'Tab': {
        if (isOpen) {
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
        break;
      }
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    if (!disabled && !isOpen) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (defaultSelectedItem?.id !== currentItem?.id) {
      setCurrentItem(defaultSelectedItem ?? null);
      setInputValue(defaultSelectedItem?.name ?? '');
    }
  }, [defaultSelectedItem?.id]);

  // Reset highlighted index when list changes
  useEffect(() => {
    setHighlightedIndex(-1);
    itemRefs.current = itemRefs.current.slice(0, filteredList.length);
  }, [filteredList.length]);

  const notFound = (
    <NotFound role='option' aria-selected='false' id='no-results'>
      <Typography variant={TextStyles['Body Text Large']}>{t(MasterTranslation.NoResultsFound)}</Typography>
    </NotFound>
  );

  const renderDropdownItemChildren = (item: T) => (
    <>
      <ImageWithFallback src={item?.logo || defaultLogo} alt={item?.name || 'Carrier Logo'} fallbackSrc={defaultLogo} />
      {item?.name}
    </>
  );

  const renderedList = (
    <ListWrapper>
      {currentItem && (
        <DropdownItem
          isActive
          onClick={() => setIsOpen(false)}
          role='option'
          aria-selected='true'
          aria-label={`Currently selected: ${currentItem.name}`}
          id={`current-${currentItem.id}`}
        >
          {renderDropdownItemChildren(currentItem)}
        </DropdownItem>
      )}
      {list
        ?.filter((item) => item?.id !== currentItem?.id)
        ?.slice(0, currentItem ? 49 : 50)
        ?.map((item: T, index: number) => (
          <DropdownItem
            // key={`${index}-${item.id}`} This is to avoid an unexpected bug on the non-prod env,
            // when the carrier data list contains more than 2 items shared same id
            key={`${index}-${item.id}`}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            onClick={() => handleSelect(item)}
            role='option'
            aria-selected={highlightedIndex === index}
            aria-label={item.name}
            isHighlighted={highlightedIndex === index}
            tabIndex={-1}
            id={`option-${item.id}`}
          >
            {renderDropdownItemChildren(item)}
          </DropdownItem>
        ))}
    </ListWrapper>
  );

  // Close dropdown when clicking outside input and list
  useClickOutsideMultiElement(
    [inputWrapperRef, dropdownListRef],
    () => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [],
  );

  // Get the active descendant ID
  const getActiveDescendant = () => {
    if (!isOpen) return undefined;
    if (highlightedIndex >= 0 && filteredList[highlightedIndex]) {
      return `option-${filteredList[highlightedIndex].id}`;
    }
    if (list?.length === 0) {
      return 'no-results';
    }
    return undefined;
  };

  return (
    <SelectorContainer aria-label={ariaLabel || ''}>
      <Dropdown>
        <DropdownInputWrapper ref={inputWrapperRef} onClick={handleInputClick} disabled={disabled}>
          <ImageWithFallback
            src={currentItem?.logo || defaultLogo}
            alt={currentItem?.name || 'Carrier Logo'}
            fallbackSrc={defaultLogo}
          />
          <DropdownInput
            ref={inputRef}
            id={comboboxId}
            placeholder={placeholder}
            onChange={handleSearch}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            disabled={disabled}
            role='combobox'
            aria-expanded={isOpen || false}
            aria-haspopup='listbox'
            aria-controls={listboxId}
            aria-activedescendant={getActiveDescendant()}
            aria-label={inputAriaLabel || ''}
            autoComplete='off'
          />
        </DropdownInputWrapper>
        {onCustomSearchClicked && (
          <SearchIconContainer
            onClick={handleCustomSearchFunction}
            disabled={disabledCustomSearch}
            role='button'
            aria-label='Search'
            tabIndex={disabledCustomSearch ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCustomSearchFunction();
              }
            }}
          >
            <SearchIcon />
          </SearchIconContainer>
        )}
        {isOpen !== null && (
          <DropdownList isOpen={isOpen} ref={dropdownListRef} tabIndex={-1}>
            {list?.length ? renderedList : notFound}
          </DropdownList>
        )}
      </Dropdown>
    </SelectorContainer>
  );
};

export default Selector;
